import React, { useContext, useState, useEffect } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import { Authen } from "api/public.js";
import { Alert, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { ConstantsContext } from "utils/const";
import { FormControl } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import OTPInput from "components/OTPInput";
import { generateUniqueId } from "utils";
import { validateWhatsappNumber } from "utils/phoneValidation";
import FlexBetween from "components/FlexBetween";
import AuthLayout, { authInputSx, authFieldLabelSx, authButtonSx } from "components/AuthLayout";
import { useTranslation } from "react-i18next";
import { markSuccessfulLogin } from "api/config";

const baseUrl = process.env.REACT_APP_BASE_URL + "/public";

export default function SignInSide({ updateCustomerState }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const constants = useContext(ConstantsContext);
  const countryRules = constants.countryRules;

  const navigate = useNavigate();
  const [loginFailed, setLoginFailed] = useState(false);
  const [otpVerifyFailed, setOtpVerifyFailed] = useState(false);
  const [otpVerifySuccess, setOptVerifySuccess] = useState(false);
  const [verifiedCustomer, setVerifiedCustomer] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [ref, setRef] = useState("");
  const [otpJobId, setOtpJobId] = useState("");
  const [otpDeliveryStatus, setOtpDeliveryStatus] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const otpLength = 6;
  const [otp, setOtp] = useState(new Array(otpLength).fill(""));

  const uniqueCountryRules = countryRules.filter(
    (country, index, self) =>
      index === self.findIndex((c) => c.phone_code === country.phone_code)
  );

  const autocompleteOptions = uniqueCountryRules.map((country) => ({
    label: `(${country.phone_code}) ${country.code} - ${country.name}`,
    value: country.phone_code,
  }));

  useEffect(() => {
    const laosRule = countryRules.find((country) => country.code === "LA");
    if (laosRule) {
      setCountryCode(laosRule.phone_code);
    }
  }, [countryRules]);

  useEffect(() => {
    if (!otpSent || !otpJobId || !phoneNumber || !ref) {
      return;
    }

    let intervalId;
    let stopped = false;

    const pollOtpDeliveryStatus = async () => {
      try {
        const response = await axios.post(
          baseUrl + "/otp-delivery-status",
          {
            jobId: otpJobId,
            phone: phoneNumber,
            ref,
            otpType: "whatsapp-login",
          },
          { withCredentials: true }
        );

        const status = response?.data?.deliveryStatus;
        setOtpDeliveryStatus(status || "");

        if (status === "failed" || status === "cancelled") {
          setOtpVerifyFailed(true);
          setErrorMsg(
            response?.data?.lastErrorMessage ||
              "OTP delivery failed. Please tap Resend OTP."
          );
          setIsResendDisabled(false);
          setTimerSeconds(0);
          clearInterval(intervalId);
        }

        if (status === "sent") {
          clearInterval(intervalId);
        }
      } catch (error) {
        if (!stopped) {
          console.log("OTP delivery status check failed:", error?.message);
        }
      }
    };

    pollOtpDeliveryStatus();
    intervalId = setInterval(pollOtpDeliveryStatus, 3000);

    return () => {
      stopped = true;
      clearInterval(intervalId);
    };
  }, [otpSent, otpJobId, phoneNumber, ref]);

  const handleOTPComplete = async (otp) => {
    console.log("Entered OTP:", otp);
    setIsLoading(true);

    await axios
      .post(baseUrl + "/otp-verify", {
        phone: phoneNumber,
        otp,
        ref,
        otpType: "whatsapp-login"
      }, {
        withCredentials: true // Enable cookies for secure authentication
      })
      .then((res) => {
        console.log(res);
        if (res && res.status === 200) {
          setOtpVerifyFailed(false);
          setOptVerifySuccess(true);
          setVerifiedCustomer(res.data.customer);
          console.log("verification successfully.");
          console.log(res.data);

          // Mark successful login to prevent immediate logout
          markSuccessfulLogin();

          // Store customer data in localStorage (tokens are now in secure cookies)
          localStorage.setItem("customer", JSON.stringify(res.data.customer));
          setTimeout(() => {
            handleOtpVerificationSuccess(res.data.customer);
          }, 5000); // Wait for 5 seconds before redirecting
        }
      })
      .catch(function (error) {
        if (error.response && error.response.status === 401) {
          console.log("OTP verification failed!");
          setOtpVerifyFailed(true);
          setOptVerifySuccess(false);
          setErrorMsg(error.response.data.message);
        }
      });
    setIsLoading(false);
  };

  const handleOtpVerificationSuccess = async (customer) => {
    updateCustomerState(customer); // Update the customer state in App.js
    if (customer.existingCustomer) {
      if (customer.emptyPassword) {
        navigate("/set-password", { state: { phoneNumber } });
      } else {
        navigate("/customer-portal");
      }
    } else {
      navigate("/register", { state: { phoneNumber } });
    }
  };

  const handleOTPRequestSubmit = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    setErrorMsg("");

    const validation = validateWhatsappNumber(
      countryRules,
      countryCode,
      whatsappNumber
    );
    if (!validation.valid) {
      setErrorMsg(validation.message);
      setLoginFailed(true);
      return;
    }

    setIsLoading(true);
    setOtpJobId("");
    setOtpDeliveryStatus("");
    const phone = validation.fullNumber;
    setPhoneNumber(phone);
    const genId = generateUniqueId(10);
    setRef(genId);

    await axios
      .post(baseUrl + "/otp-request", {
        phone,
        ref: genId,
        otpType: "whatsapp-login",
      }, {
        withCredentials: true
      })
      .then((res) => {
        if (res && res.status === 200) {
          setOtpJobId(res?.data?.jobId || "");
          setOtpDeliveryStatus("queued");
          setOtpSent(true);
          setLoginFailed(false);
          startResendTimer();
        }
      })
      .catch(function (error) {
        setOtpSent(false);
        setErrorMsg(
          error.response?.data?.message ||
            "Unable to send OTP. Please try again later."
        );
        setLoginFailed(true);
      });
    setIsLoading(false);
  };

  const handleResendOTPButton = async () => {
    setOtp(new Array(otpLength).fill(""));
    setOtpVerifyFailed(false);

    setIsLoading(true);
    const phone = phoneNumber;
    if (!phone) {
      setErrorMsg("Missing phone number — please go back and re-enter.");
      setLoginFailed(true);
      setIsLoading(false);
      return;
    }
    const genId = generateUniqueId(10);
    setRef(genId);
    setOtpDeliveryStatus("");
    setOtpJobId("");

    await axios
      .post(baseUrl + "/otp-request", {
        phone,
        ref: genId,
        otpType: "whatsapp-login"
      }, {
        withCredentials: true
      })
      .then((res) => {
        if (res && res.status === 200) {
          setOtpJobId(res?.data?.jobId || "");
          setOtpDeliveryStatus("queued");
          startResendTimer();
          setLoginFailed(false);
        }
      })
      .catch(function (error) {
        console.log(error);
        setOtpVerifyFailed(true);
        setErrorMsg("OTP request failed, please try again later");

        if (error.response && error.response.status === 400) {
          console.log("OTP request failed");
          setLoginFailed(true);
        }
      });
    setIsLoading(false);
  };

  const startResendTimer = () => {
    setTimerSeconds(60);
    setIsResendDisabled(true);
  };

  useEffect(() => {
    let intervalId;

    if (otpSent && timerSeconds > 0) {
      intervalId = setInterval(() => {
        setTimerSeconds((prevSeconds) => prevSeconds - 1);
      }, 1000);
    } else if (timerSeconds === 0) {
      setIsResendDisabled(false); // Enable resend when timer reaches 0
      clearInterval(intervalId); // Clear the interval
    }

    // Cleanup function to clear the interval when the component unmounts or otpSent changes
    return () => clearInterval(intervalId);
  }, [otpSent, timerSeconds]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      Authen(token).then((res) => {
        if (res.status === 200) {
          navigate("/tracking");
        } else {
          localStorage.removeItem("token");
          window.location = "/login";
        }
      });
    }
  }, [navigate]);

  const inputSx = authInputSx(theme);
  const fieldLabelSx = authFieldLabelSx(theme);

  const features = [
    t("clientLogin.feature1"),
    t("clientLogin.feature2"),
    t("clientLogin.feature3"),
  ];

  return (
    <AuthLayout tagline={t("clientLogin.tagline")} features={features}>
      <Box sx={{ mb: "34px" }}>
        <Typography sx={{ fontFamily: "'Space Grotesk', 'Noto Sans Lao', sans-serif", fontSize: 30, fontWeight: 700, color: "#1b1728", lineHeight: 1.2, mb: 1 }}>
          {otpSent
            ? otpVerifySuccess
              ? "Verification successful"
              : "Enter verification code"
            : "Sign in via WhatsApp"}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#9b8ab8", fontWeight: 500 }}>
          {otpSent
            ? otpVerifySuccess
              ? "Redirecting you, please wait a moment."
              : `We sent a code to your WhatsApp number ${countryCode + whatsappNumber}`
            : "We will send you a confirmation code on WhatsApp"}
        </Typography>
      </Box>

      <Box component="form" noValidate onSubmit={handleOTPRequestSubmit}>
        {!otpSent && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography sx={fieldLabelSx}>Country Code</Typography>
              <FormControl fullWidth required>
                <Autocomplete
                  id="country-code"
                  name="countryCode"
                  options={autocompleteOptions}
                  disabled={isLoading}
                  getOptionLabel={(option) => option.label}
                  value={
                    autocompleteOptions.find(
                      (option) => option.value === countryCode
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setCountryCode(newValue ? newValue.value : "");
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select country code" sx={inputSx} />
                  )}
                />
              </FormControl>
            </Box>
            <Box sx={{ mb: "14px" }}>
              <Typography component="label" htmlFor="whatsappNumber" sx={fieldLabelSx}>
                WhatsApp Number
              </Typography>
              <TextField
                required
                fullWidth
                id="whatsappNumber"
                name="whatsappNumber"
                placeholder="Enter your WhatsApp number"
                autoComplete="whatsapp"
                autoFocus
                disabled={isLoading}
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WhatsAppIcon sx={{ fontSize: 18, color: "#9d8bc4" }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputSx}
              />
            </Box>
          </>
        )}

        {loginFailed && (
          <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>
        )}

        {otpSent && !otpVerifySuccess && (
          <Box sx={{ mb: 2 }}>
            <OTPInput
              otp={otp}
              setOtp={setOtp}
              length={otpLength}
              onComplete={handleOTPComplete}
              fontSize="14px"
              boxWidth="18px"
            />
            {otpVerifyFailed && (
              <Alert severity="error" sx={{ mt: 2 }}>{errorMsg}</Alert>
            )}
            {!otpVerifyFailed && otpDeliveryStatus && otpDeliveryStatus !== "sent" && (
              <Alert severity="info" sx={{ mt: 2 }}>
                We're sending OTP to {whatsappNumber.slice(0, 3)}{"X".repeat(Math.max(0, whatsappNumber.length - 6))}{whatsappNumber.slice(-3)}
              </Alert>
            )}
            <FlexBetween sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: 13, color: "#9b8ab8" }}>Reference no. {ref}</Typography>
            </FlexBetween>
          </Box>
        )}

        {!otpSent ? (
          <Button type="submit" disabled={isLoading} fullWidth variant="contained" color="secondary" sx={authButtonSx(theme)}>
            Send OTP
          </Button>
        ) : otpVerifySuccess ? (
          <Button
            disabled={isLoading}
            fullWidth
            variant="contained"
            color="secondary"
            sx={authButtonSx(theme)}
            onClick={() => handleOtpVerificationSuccess(verifiedCustomer)}
          >
            Ok
          </Button>
        ) : (
          <Button
            disabled={isLoading || isResendDisabled}
            fullWidth
            variant="contained"
            color="secondary"
            sx={authButtonSx(theme)}
            onClick={() => handleResendOTPButton()}
          >
            {isResendDisabled ? `Resend OTP in ${timerSeconds}` : "Resend OTP"}
          </Button>
        )}
      </Box>
    </AuthLayout>
  );
}
